import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { restaurantService } from '@/services/restaurantService';
import type { Restaurant } from '@/types/restaurant';

interface RestaurantState {
  currentRestaurant: Restaurant | null;
  loading: boolean;
  error: string | null;
}

interface RestaurantActions {
  fetchRestaurant: () => Promise<void>;
  refetchRestaurant: () => Promise<void>;
  clearRestaurant: () => void;
}

type RestaurantStore = RestaurantState & RestaurantActions;

const SESSION_KEY = 'kombu-restaurant-cache';

export const useRestaurantStore = create<RestaurantStore>()(
  persist(
    (set, get) => ({
      currentRestaurant: null,
      loading: false,
      error: null,

      fetchRestaurant: async () => {
        const cached = sessionStorage.getItem(SESSION_KEY);
        if (cached) {
          try {
            const restaurant = JSON.parse(cached) as Restaurant;
            set({ currentRestaurant: restaurant, loading: false, error: null });
            return;
          } catch {
            sessionStorage.removeItem(SESSION_KEY);
          }
        }

        set({ loading: true, error: null });
        try {
          const restaurant = await restaurantService.getCurrentRestaurant();
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(restaurant));
          set({ currentRestaurant: restaurant, loading: false, error: null });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error desconocido';
          set({ currentRestaurant: null, loading: false, error: message });
        }
      },

      refetchRestaurant: async () => {
        sessionStorage.removeItem(SESSION_KEY);
        await get().fetchRestaurant();
      },

      clearRestaurant: () => {
        sessionStorage.removeItem(SESSION_KEY);
        set({ currentRestaurant: null, loading: false, error: null });
      },
    }),
    {
      name: 'kombu-restaurant-storage',
      partialize: (state) => ({
        currentRestaurant: state.currentRestaurant,
      }),
    }
  )
);

export function useRestaurant(): RestaurantStore {
  const currentRestaurant = useRestaurantStore((state) => state.currentRestaurant);
  const loading = useRestaurantStore((state) => state.loading);
  const error = useRestaurantStore((state) => state.error);
  const fetchRestaurant = useRestaurantStore((state) => state.fetchRestaurant);
  const refetchRestaurant = useRestaurantStore((state) => state.refetchRestaurant);
  const clearRestaurant = useRestaurantStore((state) => state.clearRestaurant);

  return {
    currentRestaurant,
    loading,
    error,
    fetchRestaurant,
    refetchRestaurant,
    clearRestaurant,
  };
}
